import type {
  WebsiteArticleCategoryResp,
  WebsiteArticlePageResp,
  WebsiteArticleViewResp
} from './api';

export interface ArticleCategoryCard {
  id: string;
  name: string;
  icon: string;
  description: string;
  gradient: string;
  count: number;
}

export interface ArticlePost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  author: string;
  authorRole: string;
  authorCompany: string;
  authorAvatar: string;
  coverImage: string;
  publishedAt: string;
  readTime: number;
  views: number;
  likes: number;
  featured: boolean;
  gradient: string;
}

const fallbackGradients = [
  'from-violet-600 to-indigo-700',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-rose-600',
  'from-sky-500 to-blue-600',
  'from-amber-500 to-orange-600',
  'from-pink-500 to-rose-600'
];

const fallbackIcons = [
  'ri-bar-chart-grouped-line',
  'ri-checkbox-multiple-line',
  'ri-code-s-slash-line',
  'ri-briefcase-4-line',
  'ri-user-search-line',
  'ri-article-line'
];

function pickByIndex<T>(list: T[], seed: string | number) {
  const value = Math.abs(Number(seed) || 0);
  return list[value % list.length];
}

function normalizeMediaUrl(url?: string) {
  const value = (url || '').trim();
  if (!value) {
    return '';
  }
  if (/^(https?:)?\/\//i.test(value) || /^data:/i.test(value) || /^blob:/i.test(value)) {
    return value;
  }
  if (value.startsWith('/gateway/')) {
    return value;
  }
  if (value.startsWith('/')) {
    return `/gateway${value}`;
  }
  return `/gateway/${value.replace(/^\.?\//, '')}`;
}

export function adaptArticleCategory(category: WebsiteArticleCategoryResp): ArticleCategoryCard {
  return {
    id: String(category.id),
    name: category.categoryName || '未分類',
    icon: category.icon || pickByIndex(fallbackIcons, category.id),
    description: category.categoryDesc || '精選內容',
    gradient: category.gradient || pickByIndex(fallbackGradients, category.id),
    count: Number(category.articleCount ?? 0)
  };
}

export function adaptArticlePreview(article: WebsiteArticlePageResp): ArticlePost {
  const seed = article.id ?? article.title ?? 0;
  return {
    id: String(article.id),
    title: article.title || '未命名文章',
    excerpt: article.excerpt || '',
    content: '',
    category: article.categoryName || '未分類',
    tags: article.tags || [],
    author: article.authorName || 'NexusGo',
    authorRole: article.authorRole || '',
    authorCompany: article.authorCompany || 'NexusGo',
    authorAvatar: normalizeMediaUrl(article.authorAvatar),
    coverImage: normalizeMediaUrl(article.coverImage),
    publishedAt: article.publishedAt || '',
    readTime: Number(article.readTime ?? 0),
    views: Number(article.views ?? 0),
    likes: Number(article.likes ?? 0),
    featured: Number(article.featured ?? 0) === 1,
    gradient: pickByIndex(fallbackGradients, seed)
  };
}

export function adaptArticleDetail(article: WebsiteArticleViewResp): ArticlePost {
  const preview = adaptArticlePreview(article);
  return {
    ...preview,
    content: article.content || ''
  };
}
